import { postActivity } from "@/lib/sessionChannel";

/**
 * How long since the user last did something, shared across tabs.
 *
 * Deliberately dependency-free apart from sessionChannel, so authSession can read the idle age
 * when it refreshes without an import cycle back through the idle policy.
 *
 * Wall-clock based on purpose: a suspended laptop must come back counted as idle, which is exactly
 * what a frozen setTimeout would hide.
 */

/** Other tabs only need to hear "still here" occasionally — an idle window of 10+ minutes does
 *  not care about 30 seconds, and every keystroke posting a message would be a storm for nothing. */
const ACTIVITY_BROADCAST_THROTTLE_MS = 30 * 1000;

let lastActivityAt = Date.now();
let lastBroadcastAt = 0;

/** sip-idp's own cap when the tenant sets none. */
export const DEFAULT_IDLE_LIMIT_MS = 15 * 60 * 1000;
let sessionIdleLimitMs = DEFAULT_IDLE_LIMIT_MS;

/** sip-idp clamps the pushed cap to this range; the tab must never stay up longer than the server allows. */
const MIN_IDLE_LIMIT_SEC = 10 * 60;
const MAX_IDLE_LIMIT_SEC = 24 * 60 * 60;

/** CT-372: the tenant's UI inactivity limit, as pbx-api's login/refresh envelope states it. */
export function setSessionIdleLimitSec(limitSec: number | null): void {
    sessionIdleLimitMs =
        typeof limitSec === "number" && Number.isFinite(limitSec) && limitSec > 0
            ? Math.min(Math.max(limitSec, MIN_IDLE_LIMIT_SEC), MAX_IDLE_LIMIT_SEC) * 1000
            : DEFAULT_IDLE_LIMIT_MS;
}

export function getSessionIdleLimitMs(): number {
    return sessionIdleLimitMs;
}

/** Record that the user did something. `broadcast: false` when the signal CAME from another tab —
 *  echoing it back would bounce between tabs forever. */
export function noteActivity(options: { broadcast?: boolean } = {}): void {
    const { broadcast = true } = options;
    const now = Date.now();
    lastActivityAt = now;

    if (broadcast && now - lastBroadcastAt >= ACTIVITY_BROADCAST_THROTTLE_MS) {
        lastBroadcastAt = now;
        postActivity();
    }
}

export function getIdleMs(): number {
    return Math.max(0, Date.now() - lastActivityAt);
}

/** Start the window fresh — used on login and on every established session, so a new session never
 *  inherits the previous user's idle age. */
export function resetIdleClock(): void {
    lastActivityAt = Date.now();
    lastBroadcastAt = 0;
}
